import { alpha, Box, Link, SxProps, Theme, Typography } from "@mui/material"
import { LinkButton, PrimaryButton, TextInput, TitleTertiary } from "@recall/common"
import { RESET_PATH } from "constants/routes"
import { emailValidation, passwordValidationWithoutMinLength } from "constants/validationRules"
import { useAuth } from "hooks/auth/useAuth"
import { FC, useState } from "react"
import { useForm } from "react-hook-form"
import { Link as RouterLink } from "react-router-dom"
import SignUpProvider from "../SignUpPage/SignUpProvider"

interface LoginFields {
    email: string
    password: string
}

interface Props {
    onClickSignup: () => void
}

export const LoginForm: FC<Props> = ({ onClickSignup }) => {
    const auth = useAuth()
    const [loading, setLoading] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        watch,
    } = useForm<LoginFields>({
        defaultValues: {
            email: "",
            password: "",
        },
    })

    const onSubmit = async (values: LoginFields) => {
        setLoading(true)
        try {
            await auth.signInWithEmailAndPassword(values.email.trim(), values.password)
        } catch {
            setError("email", { message: "Invalid email or password." })
            setError("password", { message: "Invalid email or password." })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <TitleTertiary mb={4} mt={1}>
                Login
            </TitleTertiary>
            <SignUpProvider />
            <Typography py={3} variant="body1" color="primary.light">
                OR
            </Typography>
            <Box component="form" sx={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <TextInput
                    fullWidth
                    {...register("email", emailValidation)}
                    label="Email Address"
                    variant="outlined"
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    data-testid="email"
                />
                <TextInput
                    fullWidth
                    {...register("password", passwordValidationWithoutMinLength)}
                    label="Password"
                    type="password"
                    variant="outlined"
                    error={Boolean(errors.password)}
                    helperText={
                        <Box component="span" sx={styles.helperText}>
                            {errors.password?.message && <>{errors.password.message}</>}
                            <Link
                                component={RouterLink}
                                to={`${RESET_PATH}?email=${encodeURIComponent(watch("email"))}`}
                                sx={styles.link}
                            >
                                Forgot your password?
                            </Link>
                        </Box>
                    }
                    data-testid="password"
                />
                <PrimaryButton sx={{ my: 2 }} loading={loading} fullWidth type="submit">
                    Login
                </PrimaryButton>
            </Box>
            <Box mt={4}>
                <LinkButton onClick={onClickSignup}>Don't have an account? Sign Up</LinkButton>
            </Box>
        </>
    )
}

const styles: Record<string, SxProps<Theme>> = {
    helperText: {
        display: "flex",
        gap: 4,
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    link: {
        mt: 0.5,
        color: (theme) => alpha(theme.palette.primary.light, 0.9),
        textDecoration: "none",
        "&:hover": {
            color: (theme) => theme.palette.primary.light,
        },
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        width: "100%",
    },
}
